import React, { useState } from 'react';
import { observer } from 'mobx-react';
import {
  IconType, ButtonProps, Icon, notification, NotificationType,
} from '@routific/hive';
import { useTranslation } from 'react-i18next';
import { ClientError } from 'graphql-request';
import { useStore } from '../../../../store/mst';
import ActionButton, { CommonActionTypes, ButtonTypes } from '../../../shared/ActionButton';
import { RouteProps } from '../../../../store/mst/utils/getRoutesProps';
import usePermissions, { Features } from '../../../../permissions/usePermissions';
import ComponentNames from '../../../../permissions/constants/componentNames';
import downloadSolutionCsv from '../../DownloadSolution/helpers/downloadSolutionCsv';
import { parseDownloadErrors } from '../../DownloadSolution/helpers/utils';
import { DownloadCSVAPIError } from '../../DownloadSolution/types';
import useExportColumns from '../../DownloadSolution/hooks/useExportColumns';
import DownloadCSVModal from '../../DownloadSolution/DownloadCSVModal';
import RouteSolutionDownloadQuery from '../../../../store/mst/queries/routes/RouteSolutionDownloadQuery';
import { ExportColumnGroup } from '../../../../store/mst/models';

interface Props {
  routeProps: RouteProps;
  projectId: number;
  type?: ButtonTypes;
  buttonProps?: ButtonProps;
  disabled?: boolean;
}

const DownloadRouteButton: React.FC<Props> = ({
  routeProps,
  projectId,
  type,
  buttonProps,
  disabled,
}) => {
  const store = useStore();
  const { t } = useTranslation('components');
  const [isDownloading, setIsDownloading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [downloadError, setDownloadError] = useState<DownloadCSVAPIError | null>(null);

  const { canDownloadSolution } = usePermissions(Features.Routes, ComponentNames.DownloadCSV);

  const routeUuid = routeProps.uuids[0];

  const exportColumns = useExportColumns(visible);

  const handleClickDownload = (e?: any) => {
    e?.stopPropagation?.();

    if (!routeUuid || !canDownloadSolution || isDownloading) {
      return;
    }

    setVisible(true);
  };

  const onCancel = () => {
    setVisible(false);
    setDownloadError(null);
  };

  const handleToggleColumn = (key: string) => {
    setDownloadError(null);
    exportColumns.toggleColumn(key);
  };

  const handleToggleGroup = (group: ExportColumnGroup) => {
    setDownloadError(null);
    exportColumns.toggleGroup(group);
  };

  const handleExport = async () => {
    if (!routeUuid || !canDownloadSolution || isDownloading) {
      return;
    }

    const id = `download-route-${routeUuid}-${new Date().toUTCString()}`;

    try {
      setIsDownloading(true);
      const queryResult = await store.model.queryRouteSolutionDownload(
        {
          projectId,
          routeUuid,
          columns: exportColumns.selectedColumns,
        },
        RouteSolutionDownloadQuery,
      );

      if (!queryResult.routeSolutionDownload.url) {
        throw new Error('Failed to download route solution');
      }

      downloadSolutionCsv(queryResult.routeSolutionDownload.url);

      setDownloadError(null);
      setVisible(false);
      notification({
        type: NotificationType.SUCCESS,
        id,
        message: t('moreActionsButton.route.button.download.notification.success'),
        dataCy: 'download-route-success-notification',
      });
    } catch (err) {
      if (err instanceof ClientError) {
        const code = parseDownloadErrors(err);
        setDownloadError(code === 'noOrders' ? 'noOrdersRoute' : code);
      } else {
        notification({
          type: NotificationType.ERROR,
          id,
          message: t('moreActionsButton.route.button.download.notification.error'),
          dataCy: 'download-route-error-notification',
        });
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const getTooltipText = () => {
    if (!canDownloadSolution) {
      return t('moreActionsButton.route.button.download.tooltip.noPermission');
    }
    if (type === ButtonTypes.BUTTON) {
      return t('moreActionsButton.route.button.download.tooltip.button');
    }
    return undefined;
  };

  return (
    <>
      <div onClick={(e) => e.stopPropagation()}>
        <DownloadCSVModal
          visible={visible}
          onCancel={onCancel}
          onOk={handleExport}
          downloadError={downloadError}
          isLoading={isDownloading}
          groups={exportColumns.groups}
          selectedKeys={exportColumns.selectedKeys}
          hasSelection={exportColumns.hasSelection}
          hasError={exportColumns.hasError}
          toggleColumn={handleToggleColumn}
          toggleGroup={handleToggleGroup}
          isGroupChecked={exportColumns.isGroupChecked}
          isGroupIndeterminate={exportColumns.isGroupIndeterminate}
        />
      </div>
      <ActionButton
        name={CommonActionTypes.DOWNLOAD}
        type={type}
        disabled={disabled || !canDownloadSolution || isDownloading}
        tooltipProps={{
          title: getTooltipText(),
        }}
        menuItemText={t('moreActionsButton.route.button.download.text')}
        menuItemProps={{
          onClick: handleClickDownload,
          icon: <Icon icon={IconType.Download} />,
        }}
        buttonProps={{
          icon: IconType.Download,
          onClick: handleClickDownload,
          ...buttonProps,
        }}
      />
    </>
  );
};

export default observer(DownloadRouteButton);
